import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type ConfirmationModalProps = {
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  children: React.ReactNode;
  titleKey?: string;
  childrenKey?: string;
};

const ConfirmationModal = ({
  isOpen,
  isLoading,
  onClose,
  onConfirm,
  title,
  children,
  titleKey,
  childrenKey,
}: ConfirmationModalProps) => {
  const { t } = useTranslation();

  const resolvedTitle = titleKey ? t(titleKey) : title;

  const resolvedChildren = childrenKey
    ? t(childrenKey)
    : typeof children === "string" &&
        (children === "Aucune modification" ||
          children === "No changes" ||
          children === "No changes detected")
      ? t("no_changes")
      : children;

  return (
    <Modal
      backdrop="blur"
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader>{resolvedTitle}</ModalHeader>
        <ModalBody>{resolvedChildren}</ModalBody>
        <ModalFooter>
          <Button color="primary" variant="flat" onPress={onClose}>
            {t("actions.cancel")}
          </Button>
          <Button color="primary" isLoading={isLoading} onPress={onConfirm}>
            {t("actions.confirm", { defaultValue: "Confirm" })}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

type DeleteConfirmationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onDelete: () => void;
  isLoading?: boolean;
  body?: string;
  requireTyping?: boolean;
  confirmationText?: string;
};

const DeleteConfirmationModal = ({
  isOpen,
  onClose,
  onDelete,
  isLoading = false,
  body,
  requireTyping = false,
  confirmationText,
}: DeleteConfirmationModalProps) => {
  const { t } = useTranslation();
  const [typed, setTyped] = useState("");
  const effectiveConfirmationText =
    confirmationText ??
    t("delete_confirm.confirmation_word", { defaultValue: "delete" });

  const handleClose = () => {
    setTyped("");
    onClose();
  };

  const handleDelete = () => {
    onDelete();
  };

  return (
    <Modal backdrop="blur" isOpen={isOpen} onClose={handleClose}>
      <ModalContent>
        <ModalHeader>
          {t("delete_confirm.title", { defaultValue: "Confirm deletion" })}
        </ModalHeader>
        <ModalBody className="flex flex-col gap-3 text-gray-600">
          <p>
            {body ??
              t("delete_confirm.body", {
                defaultValue: "Are you sure you want to delete this item?",
              })}
          </p>
          {requireTyping && (
            <Input
              size="sm"
              placeholder={t("delete_confirm.type_to_confirm", {
                word: effectiveConfirmationText,
                defaultValue: "Type « {{word}} » to confirm",
              })}
              value={typed}
              onValueChange={setTyped}
            />
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" variant="flat" onPress={handleClose}>
            {t("actions.cancel")}
          </Button>
          <Button
            color="danger"
            isLoading={isLoading}
            isDisabled={
              isLoading || (requireTyping && typed !== effectiveConfirmationText)
            }
            onPress={handleDelete}
          >
            {t("actions.delete")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

type CancelConfirmationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onCancel: () => void;
};

const CancelConfirmationModal = ({
  isOpen,
  onClose,
  onCancel,
}: CancelConfirmationModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal backdrop="blur" isOpen={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalHeader>
          {t("cancel_confirm.title", {
            defaultValue: "Confirm cancellation",
          })}
        </ModalHeader>
        <ModalBody className="text-gray-600">
          {t("cancel_confirm.body", {
            defaultValue:
              "Are you sure you want to cancel creation? All entered data will be lost.",
          })}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" variant="light" onPress={onClose}>
            {t("cancel_confirm.continue", {
              defaultValue: "Continue editing",
            })}
          </Button>
          <Button color="danger" onPress={onCancel}>
            {t("cancel_confirm.cancel", {
              defaultValue: "Cancel creation",
            })}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export { CancelConfirmationModal, ConfirmationModal, DeleteConfirmationModal };
